import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { MoreVertical, Pencil, Plus, Loader2 } from 'lucide-react'
import { useBankAccounts, useFacilities, usePacks } from '@/hooks/useBanking'
import { AddFacilityDialog } from '@/components/banking/AddFacilityDialog'
import { EditFacilityDialog } from '@/components/banking/EditFacilityDialog'
import type { Facility } from '@/types/banking'
import { financialAccountsBasePath } from '@/components/financial-accounts/financialAccountsScope'
import { FacilitiesFilters } from '@/components/banking/FacilitiesFilters'
import type { FacilitiesFilterState } from '@/components/banking/FacilitiesFilters'
import {
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Pagination,
  PageHeader,
} from '@atrvd/saas-ui'

const ITEMS_PER_PAGE = 10

/**
 * PostgREST's hard response cap (`max_rows` in supabase/config.toml). A response
 * holding exactly this many rows cannot be distinguished from a truncated one,
 * so any aggregate over it is untrustworthy.
 */
const POSTGREST_MAX_ROWS = 1000

/**
 * Renders one facility's bank-account count.
 *
 * DCP-260: purely presentational — it takes an already-resolved count instead
 * of running its own query. The previous version called a per-row hook, which
 * meant one request per visible facility (N+1); the page now does a single
 * org-wide fetch and passes the tallied number down.
 *
 * The state is driven by whether the count is KNOWN, not by a loading flag:
 * `count === null` means "not known yet". "0" must mean "this facility has no
 * bank accounts" and nothing else — a placeholder zero standing in for unknown
 * data is precisely what let DCP-260 go unnoticed for two months, so the
 * unknown case renders a spinner instead.
 *
 * Keying on data presence rather than on React Query's `isLoading` also covers
 * the states that flag gets wrong: a query that is disabled (no organization
 * selected) reports `isLoading === false` with no data, which would have
 * rendered a false 0.
 *
 * @param count - Bank accounts linked to the facility, or null if not yet known.
 * @param unavailableReason - Why no trustworthy number can be shown, or null
 *   when the count is trustworthy. Doubles as the cell's tooltip so the dash is
 *   never unexplained.
 */
function BankAccountCountCell({
  count,
  unavailableReason,
}: {
  count: number | null
  unavailableReason: string | null
}) {
  if (unavailableReason !== null) {
    // `title` alone is not an accessible name: screen readers announce it
    // inconsistently and touch devices never surface it without a long press.
    // The dash is decorative once the reason is exposed as real text, so it is
    // hidden from assistive tech and the reason is carried by an sr-only span —
    // the same treatment the loading state gets below.
    return (
      <span className="text-muted-foreground" title={unavailableReason}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">{unavailableReason}</span>
      </span>
    )
  }

  if (count === null) {
    return (
      <>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading bank account count</span>
      </>
    )
  }

  return <span>{count}</span>
}

/**
 * Settings page showing list of facilities in a table format
 */
export function SettingsFacilitiesPage() {
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  // Add dialog state — single open flag mirroring the EditFacilityDialog
  // contract; the dialog owns its own form via react-hook-form, so the
  // page no longer has to manage form/validation state itself.
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  // Edit dialog state — single source of truth for both the open flag and the
  // currently-edited facility. `editingFacility` is cleared on close so the
  // form doesn't flash stale data the next time the dialog opens for a
  // different row.
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null)

  /** Active filter state — drives the client-side filteredFacilities memo below. */
  const [filters, setFilters] = useState<FacilitiesFilterState>({
    search: '',
    status: 'all',
    packId: 'all',
  })

  // Management page: must list ALL facilities (active AND inactive) so users can
  // see and toggle deactivated rows — the one selector that opts out of the
  // DCP-292 active-only default. Its own status filter (active/inactive/all) is
  // applied client-side below.
  const { data: facilities, isLoading, error } = useFacilities({ activeOnly: false })
  // Packs are loaded to resolve each facility's pack_id → pack name for the
  // read-only "Pack" column. Membership itself is edited on the Packs page.
  const { data: packs } = usePacks()
  // Rebuilt only when packs change (not on every render — e.g. pagination/hover).
  const packNameById = useMemo(
    () => new Map((packs ?? []).map((p) => [p.id, p.name])),
    [packs],
  )

  // DCP-260: the "Bank Accounts" column used to count Plaid items, an
  // integration the team dropped on 2026-06-15 — so it read 0 for every
  // facility no matter how many accounts existed. Bank accounts now live in a
  // single table (`bank_accounts`, DCP-217 collapse): manually added rows and
  // LedgerSync-connected rows alike, each carrying `facility_id`. One org-wide
  // fetch therefore covers both kinds the ticket asks for, and shares its cache
  // entry with the dialogs that already load this list.
  //
  // `activeOnly` is deliberately left off (defaults to false): this is the
  // management page — it lists DEACTIVATED facilities too (the useFacilities
  // call above opts out of the active-only default), and those rows must show
  // their real counts, not a hidden zero.
  const { data: bankAccounts, error: bankAccountsError } = useBankAccounts()

  /**
   * Why the column may have to refuse to show a number.
   *
   * - Failed fetch with nothing cached. A refetch that fails ON TOP of cached
   *   rows leaves React Query with BOTH `data` and `error` set; the cached count
   *   is still the best answer available, so that case keeps rendering it.
   * - Possibly-truncated list. PostgREST caps every response at
   *   `max_rows = 1000` (supabase/config.toml) and signals the cut only through
   *   HTTP 206, which `bankAccounts.list()` does not surface — so a completely
   *   full page means the org-wide list MAY be short and any tally over it would
   *   undercount the facilities that sort late by name. DCP-260 exists because
   *   this column showed a confident wrong number; a silently truncated total is
   *   the same defect, so it is reported as unknown instead. (The underlying
   *   helper is shared with other unpaginated consumers — fixing it at the
   *   source needs a paginated read or a `GROUP BY` RPC, tracked separately.)
   */
  const bankAccountsUnavailable = useMemo(() => {
    if (bankAccountsError && !bankAccounts) return 'Failed to load bank accounts'
    if (bankAccounts && bankAccounts.length >= POSTGREST_MAX_ROWS) {
      return 'Too many bank accounts to count reliably'
    }
    return null
  }, [bankAccounts, bankAccountsError])

  /**
   * facility_id → number of its bank accounts, or null while unknown.
   *
   * Counts every non-deleted account — `bankAccounts.list()` already filters
   * `deleted_at IS NULL` (DCP-222), which is this product's real "the account
   * exists" test.
   *
   * The money tiles deliberately count a NARROWER set: `scopedActiveAccountIds`
   * adds `status = 'active'` AND, with no explicit facility filter, restricts to
   * accounts under ACTIVE facilities (DCP-292). The second half diverges from
   * this column today and by design — a DEACTIVATED facility legitimately shows
   * N accounts here while every KPI tile and cash-position RPC attributes 0
   * accounts and $0 to it. That is the point of this page: you manage the rows
   * the rest of the product has stopped counting.
   *
   * The `status` half cannot diverge today: nothing in the app ever writes the
   * other `public.account_status` values ('inactive' / 'closed'), every row is
   * created at the 'active' default. If a UI to close an account is ever added,
   * revisit whether a closed account should still be counted here.
   *
   * It is NOT simply "whatever the facility's Financial Accounts page lists". An ORPHANED
   * account — `integration_id` set, `ledgersync_connection_id` NULL — falls out
   * of BOTH sections there: the LedgerSync list requires a connection id, the
   * Manual list requires `integration_id IS NULL`. Counting it here is
   * deliberate; this page is where a row the rest of the product no longer
   * shows still has to be visible.
   *
   * DCP-338 changed both how such rows arise and what they weigh, so the older
   * reasoning behind this tally no longer holds as written. Deleting a
   * connection no longer leaves accounts behind: the `delete-connection` edge
   * action (DCP-324 AC#7, `_shared/ledgersync-connection-delete.ts`) SOFT-deletes
   * the connection AND its `bank_accounts`, hard-deletes those accounts'
   * `balances`, and KEEPS the transactions — which stay readable through the
   * soft-deleted account rows. Because the connection row is soft-deleted rather
   * than removed, the `ON DELETE SET NULL` FK never fires, so that path cannot
   * mint a new orphan; and the soft-deleted accounts leave this tally anyway,
   * since the list above already filters them. A migration cleaned up the
   * orphans the old SET-NULL path had already created.
   *
   * Orphans can still appear (a reconnect reuses the connection row without
   * pruning what the previous link materialised), and DCP-338 now EXCLUDES them
   * from the money surfaces: the Bank Balance tile, the Bank Balance Update
   * table and the KPI aggregates all skip an integration-backed account whose
   * connection is gone. So this column deliberately counts a row whose money is
   * counted nowhere. That asymmetry is the point of the column — do not
   * "resolve" it by hiding the row here, which would make it invisible
   * everywhere.
   *
   * Accounts with no facility (`facility_id IS NULL` — unassigned, not
   * inactive) are counted for nobody rather than being attributed to some
   * arbitrary row.
   */
  const bankAccountCountByFacility = useMemo(() => {
    // null, not an empty Map: "not loaded yet" must stay distinguishable from
    // "loaded, and this facility has none" all the way down to the cell.
    if (!bankAccounts) return null
    const counts = new Map<string, number>()
    for (const account of bankAccounts) {
      if (!account.facility_id) continue
      counts.set(account.facility_id, (counts.get(account.facility_id) ?? 0) + 1)
    }
    return counts
  }, [bankAccounts])

  /**
   * Client-side filter applied to the raw facilities list.
   *
   * Three independent predicates, all of which must pass:
   * - name:   case-insensitive substring match on facility.name only.
   * - status: active/inactive toggle; 'all' skips the check.
   * - pack:   'none' matches pack_id === null (not assigned to any pack);
   *           a specific pack id matches exactly; 'all' skips the check.
   *
   * Re-computed only when the source list or any filter value changes.
   * Declared before the early-return guards to satisfy the Rules of Hooks
   * (hooks must not be called conditionally).
   */
  const filteredFacilities = useMemo(() => {
    const list = facilities ?? []
    // Normalize the search query once, outside the per-row loop, so trim() runs
    // a single time instead of twice per facility (DRY). An empty query (after
    // trimming) disables the name predicate entirely.
    const query = filters.search.trim().toLowerCase()
    return list.filter((f) => {
      // Name predicate — name field only, not code or sage_location_id
      if (query && !f.name.toLowerCase().includes(query)) return false

      // Status predicate
      if (filters.status === 'active' && !f.is_active) return false
      if (filters.status === 'inactive' && f.is_active) return false

      // Pack predicate — split per case so each branch is explicit:
      //   'none'          → only facilities with no pack assigned (pack_id IS NULL)
      //   a specific pack → exact match on pack_id
      //   'all'           → no constraint
      if (filters.packId === 'none') {
        if (f.pack_id !== null) return false
      } else if (filters.packId !== 'all') {
        if (f.pack_id !== filters.packId) return false
      }

      return true
    })
  }, [facilities, filters])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load facilities</p>
      </div>
    )
  }

  const totalPages = Math.ceil(filteredFacilities.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const paginatedFacilities = filteredFacilities.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Facilities (${filteredFacilities.length})`}
        description="Manage your organization's facilities"
        actions={
          <Button className="bg-red-700 hover:bg-red-800" onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Facility
          </Button>
        }
      />

      {/* Filters — reset to page 1 on any change so pagination stays valid */}
      <FacilitiesFilters
        filters={filters}
        onFiltersChange={(f) => {
          setFilters(f)
          setCurrentPage(1)
        }}
        packs={packs ?? []}
      />

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Sage Facility ID</TableHead>
              <TableHead className="font-semibold">Facility Name</TableHead>
              <TableHead className="font-semibold">Source</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Pack</TableHead>
              <TableHead className="font-semibold">Bank Accounts</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedFacilities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {/* Show a filter-specific message when any predicate is active */}
                  {filters.search.trim() || filters.status !== 'all' || filters.packId !== 'all'
                    ? 'No facilities match your filters'
                    : 'No facilities found'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedFacilities.map((facility) => (
                <TableRow key={facility.id} className="hover:bg-muted/30">
                  <TableCell className="font-mono">
                    {facility.sage_location_id || facility.code || '-'}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => navigate(financialAccountsBasePath({ facilityId: facility.id }))}
                      className="text-left hover:underline font-medium"
                    >
                      {facility.name}
                    </button>
                  </TableCell>
                  <TableCell>
                    {/* Source (DCP-262): Sage-imported vs. manually added. */}
                    <Badge
                      variant="outline"
                      className={
                        facility.source === 'sage_intacct'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-gray-50 text-gray-700 border-gray-200'
                      }
                    >
                      {facility.source === 'sage_intacct' ? 'Sage Intacct' : 'Manual'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        facility.is_active
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-gray-50 text-gray-700 border-gray-200"
                      }
                    >
                      {facility.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(facility.pack_id && packNameById.get(facility.pack_id)) || '-'}
                  </TableCell>
                  <TableCell>
                    <BankAccountCountCell
                      count={
                        bankAccountCountByFacility === null
                          ? null
                          : (bankAccountCountByFacility.get(facility.id) ?? 0)
                      }
                      unavailableReason={bankAccountsUnavailable}
                    />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setEditingFacility(facility)
                            setEditDialogOpen(true)
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(financialAccountsBasePath({ facilityId: facility.id }))}>
                          Manage Financial Accounts
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            maxPageButtons={5}
          />
        </div>
      )}

      {/* Add Facility Dialog — mirrors the Edit dialog shape (react-hook-form
          + shared facilityFormSchema) so the two flows can't drift on
          validation / field set / UX behaviour. */}
      <AddFacilityDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />

      {/* Edit Facility Modal */}
      <EditFacilityDialog
        open={editDialogOpen}
        onOpenChange={(o) => {
          setEditDialogOpen(o)
          // Clear the selected facility on close so the next open starts fresh.
          if (!o) setEditingFacility(null)
        }}
        facility={editingFacility}
      />
    </div>
  )
}
